import React, { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { utcToLocalString } from "../lib/DateToday";
import Media from "react-media";

type EventDescription = { da?: string; de?: string; en?: string };

type EventItem = {
  id?: string;
  parkingLotId: string | number;
  startTime: string;
  endTime: string;
  description?: string | EventDescription;
  eventType?: number;
};

type Lot = { id: string; name: string };

type Props = {
  events?: EventItem[];
  lots?: Lot[];
  lang?: "da" | "en" | "de";
};

export default function EventsOverlay({
  events = [],
  lots = [],
  lang = "da",
}: Props) {
  const [visible, setVisible] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const [leafletControlBar, setLeafletControlBar] = useState<HTMLElement | null>(null);
  const [originalDisplayStyle, setOriginalDisplayStyle] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = document.querySelector(".leaflet-top.leaflet-left .leaflet-control.leaflet-bar") as HTMLElement | null;
    setLeafletControlBar(ctrl);
    if (ctrl && originalDisplayStyle === null) {
      setOriginalDisplayStyle(ctrl.style.display || "");
    }
  }, [originalDisplayStyle]);

  useEffect(() => {
    if (!leafletControlBar) return;
    if (!visible) {
      leafletControlBar.style.display = "flex";
      leafletControlBar.style.alignItems = "flex-start"; 
      leafletControlBar.style.gap = leafletControlBar.style.gap || "6px";
    } else {
      if (originalDisplayStyle !== null) {
        leafletControlBar.style.display = originalDisplayStyle;
      } else {
        leafletControlBar.style.removeProperty("display");
      }
      leafletControlBar.style.removeProperty("align-items");
      leafletControlBar.style.removeProperty("gap");
    }
  }, [leafletControlBar, visible, originalDisplayStyle]);

  useEffect(() => {
    const stored = sessionStorage.getItem("eventsOverlayVisible");
    if (stored === "false") {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem("eventsOverlayVisible", String(visible));
  }, [visible]);

  const activeEvents = useMemo(() => {
    const now = Date.now();
    return events.filter((e) => {
      const s = new Date(e.startTime).getTime();
      const t = new Date(e.endTime).getTime();
      return s <= now && now <= t;
    });
  }, [events]);

  // Denne useEffect er for at forhindre scrolling af baggrunden,
  //  - når event overlayet er åbent, og når der scrolles på den øverste del af event overlayet
  // Dette var et problem på iOS enheder
  useEffect(() => {
  const body = document.body;
  const html = document.documentElement;

  if (visible) {
    const y = window.scrollY; // Gemmer den nuværende scroll position
    body.dataset.scrollY = String(y);

    // Låser body, så der ikke kan scrolles på øverste del af event overlayet
    body.style.position = "fixed";
    body.style.top = `-${y}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
  }

  // Fjerner låsen af body hvis ikke event overlayet er åbent
  return () => {
    if (visible) {
      const y = Number(body.dataset.scrollY || 0);
      body.style.removeProperty("position");
      body.style.removeProperty("top");
      body.style.removeProperty("left");
      body.style.removeProperty("right");
      body.style.removeProperty("width");
      body.style.removeProperty("overflow");

      html.style.removeProperty("overscroll-behavior");

      window.scrollTo(0, y);
      delete body.dataset.scrollY;
    }
  };
}, [visible]);

  type Group = { lotId: string; lotName: string; items: EventItem[] };
  const groups: Group[] = useMemo(() => {
    const byId = new Map<string, Group>();
    for (const ev of activeEvents) {
      const id = String(ev.parkingLotId);
      const name = lots.find((l) => String(l.id) === id)?.name ?? `Plads ${id}`;
      if (!byId.has(id)) byId.set(id, { lotId: id, lotName: name, items: [] });
      byId.get(id)!.items.push(ev);
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.lotName.localeCompare(b.lotName, "da")
    );
  }, [activeEvents, lots]);

  const toggle = (id: string) => setOpenId((currentvalue) => (currentvalue === id ? null : id));

  const description = (d?: string | EventDescription): string => {
    if (!d) return "Ingen beskrivelse";
    if (typeof d === "string") return d;
    const val = d[lang];
    return typeof val === "string" && val.trim() ? val : "Ingen beskrivelse";
  };

  if (activeEvents.length === 0) return null;

  if (!visible) {
    const EventInformationButton_Closed = (
      <div style={{ display: "inline-flex" }}>
        <button onClick={() => setVisible(true)} className="eventInformationButton_Closed">
          <i className="material-icons" aria-hidden="true" style={{ fontSize: "34px" }}>event</i>
        </button>
      </div>
    );

    return createPortal(
      <Media queries={{ mobile: "(max-width: 580px)", notMobile: "(min-width: 581px)" }}>
        {matches => (
          <Fragment>
            {matches.mobile && (
              <button onClick={() => setVisible(true)} className="eventInformationButton_Closed_mobile">
                <i className="material-icons" style={{ fontSize: "46px" }}>event</i>
              </button>
            )}
            {matches.notMobile && (
              leafletControlBar ? createPortal(EventInformationButton_Closed, leafletControlBar) : null
            )}
          </Fragment>
        )}
      </Media>,
      document.body
    );
  }

  const overlay = (
    <Media queries={{ mobile: "(max-width: 580px)", notMobile: "(min-width: 581px)" }}>
      {matches => (
        <div className="event-overlay"
        >
          {/* Luk-knap */}
          <div className="event-overlay_close_button" onClick={() => setVisible(false)}>
            <i className="material-icons" style={{ fontSize: "24px", color: "#333" }} dangerouslySetInnerHTML={{ __html: "&#xe5cd;" }} />
          </div>

          {/* Header */}
          <div style={{ position: "relative", padding: "0px", width: "210px", margin: "0 auto 20px", flex: "0 0 auto" }}>
            <h2 className="event-overlay_title">
              Parkeringsinformation
            </h2>
            <p className="event-overlay_sub_text">
              Følgende parkeringsområder er påvirket af events, vejarbejde eller
              midlertidige ændringer i reglerne:
            </p>
          </div>

          {/* Dropdown */}
          <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
            <div style={{ display: "grid", gap: "10px" }}>
              {groups.map((g) => {
                const isOpen = openId === g.lotId;
                return (
                  <div key={g.lotId} className="event-overlay_dropdown">
                    <button
                      onClick={() => toggle(g.lotId)} className="event-overlay_dropdown_inside"
                      aria-expanded={isOpen}
                      aria-controls={`panel-${g.lotId}`}
                    >
                      <div className="event-overlay_parkinglot_name">
                        {g.lotName}
                      </div>
                      <div
                        aria-hidden
                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", color: "#333", fontSize: "12px", }}
                      >
                        ▼
                      </div>
                    </button>

                    {isOpen && (
                      <div id={`panel-${g.lotId}`} className="event-overlay_dropdown_content_background">
                        {g.items.map((ev) => (
                          <div key={ev.id ?? `${g.lotId}-${ev.startTime}`} className="event-overlay_dropdown_content_bottom">
                            <div className="event-overlay_dropdown_content_top">
                              <div className="event-overlay_dropdown_content_top_left">
                                <i className="material-icons" style={{ fontSize: "21px", color: "#333" }}>&#xe88e;</i>
                                <span className="event-overlay_dropdown_content_top_left_text">
                                  Event
                                </span>
                              </div>
                              <div className="event-overlay_dropdown_content_top_right_text">
                                <div>
                                  <span style={{ fontWeight: 600 }}>Start:</span>{" "}
                                  <span>{utcToLocalString(ev.startTime)}</span>
                                </div>
                                <div>
                                  <span style={{ fontWeight: 600 }}>Slut:</span>{" "}
                                  <span>{utcToLocalString(ev.endTime)}</span>
                                </div>
                              </div>
                            </div>
                            <div style={{ marginTop: 4, fontSize: 12, fontFamily: "sans-serif" }}>
                              {description(ev.description)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Media>
  );

  return createPortal(overlay, document.body);
}